import { AxiosResponse, isAxiosError } from "axios"
import { useCallback, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import {
  getQueries,
  saveQueryfile,
  deleteQueryfile,
  getQueryfile,
  saveQueryfileMeta,
  getQueryfileMeta,
  getQueryfileMetaList,
  QueryfileMetaListEntry,
} from "api/Repos/queries"
import {
  QueryFileSaveResponse,
  QueryFileDeleteResponse,
} from "types/query.types"
import { EnvelopeMeta, SavedQueryEnvelope } from "types/savedQuery.types"
import { tryUnwrap, wrapForSave } from "utils/queryEnvelope"
import { errMsgOf } from "utils/errMsgOf"

/**
 * Saved-queries domain hook (formerly `useQueries` — renamed to avoid
 * clashing with @tanstack/react-query's own useQueries export).
 *
 * The structured reads (`listStructured`, `getStructured`) are cached in the
 * react-query cache via `queryClient.fetchQuery`, which supplies the
 * single-flight dedup and the 5s list TTL that used to be a hand-rolled
 * module-level cache here. `saveStructured`/`deleteQuery` update that cache
 * in place (setQueryData upsert/remove), so post-save refresh paths hit the
 * cache instead of re-fetching every envelope.
 *
 * The raw byte-level operations (getProjectQueries, getQuery) stay
 * imperative command calls built directly on `useAuthedAxios` — they're used
 * inside Console flows that need the response inline. No `useApiCall` here:
 * that legacy wrapper only remains for not-yet-migrated domains.
 */

/** Freshness window for the structured list (mirrors the old TTL cache). */
const LIST_STALE_MS = 5_000

export const useSavedQueries = () => {
  const queryClient = useQueryClient()
  const getAxios = useAuthedAxios()

  const [isLoadingQueries, setIsLoadingQueries] = useState(false)

  /** Raw newline-separated names list; loading state feeds the Console UI. */
  const getProjectQueries = useCallback(
    async (
      projectId: string,
      owner: string
    ): Promise<AxiosResponse<string>> => {
      setIsLoadingQueries(true)
      try {
        return await getQueries(await getAxios(), projectId, owner)
      } finally {
        setIsLoadingQueries(false)
      }
    },
    [getAxios]
  )

  const saveQuery = useCallback(
    async (
      projectId: string,
      owner: string,
      name: string,
      overwrite: string,
      body: string
    ): Promise<AxiosResponse<QueryFileSaveResponse>> =>
      saveQueryfile(await getAxios(), projectId, owner, name, overwrite, body),
    [getAxios]
  )

  const saveQueryMeta = useCallback(
    async (
      projectId: string,
      owner: string,
      name: string,
      sidecar: string
    ): Promise<AxiosResponse<QueryFileSaveResponse>> =>
      saveQueryfileMeta(await getAxios(), projectId, owner, name, sidecar),
    [getAxios]
  )

  const getQuery = useCallback(
    async (
      projectId: string,
      owner: string,
      name: string
    ): Promise<AxiosResponse<string>> =>
      getQueryfile(await getAxios(), projectId, owner, name),
    [getAxios]
  )

  const getQueryMeta = useCallback(
    async (
      projectId: string,
      owner: string,
      name: string
    ): Promise<AxiosResponse<string>> =>
      getQueryfileMeta(await getAxios(), projectId, owner, name),
    [getAxios]
  )

  const getQueryMetaList = useCallback(
    async (
      projectId: string,
      owner: string
    ): Promise<AxiosResponse<QueryfileMetaListEntry[]>> =>
      getQueryfileMetaList(await getAxios(), projectId, owner),
    [getAxios]
  )

  const deleteQueryRaw = useCallback(
    async (
      projectId: string,
      owner: string,
      name: string
    ): Promise<AxiosResponse<QueryFileDeleteResponse>> =>
      deleteQueryfile(await getAxios(), projectId, owner, name),
    [getAxios]
  )

  /** Deletes a saved query and drops it from the cached structured list. */
  const deleteQuery = useCallback(
    async (
      projectId: string,
      owner: string,
      name: string
    ): Promise<AxiosResponse<QueryFileDeleteResponse>> => {
      const result = await deleteQueryRaw(projectId, owner, name)
      queryClient.setQueryData<SavedQueryEnvelope[]>(
        queryKeys.savedQueries.list(projectId, owner),
        (old) => old?.filter((e) => e.name !== name)
      )
      queryClient.removeQueries({
        queryKey: queryKeys.savedQueries.detail(projectId, owner, name),
      })
      // The names list (Provenance lineage) is read again
      queryClient.invalidateQueries({
        queryKey: queryKeys.savedQueries.names(projectId, owner),
      })
      return result
    },
    [deleteQueryRaw, queryClient]
  )

  /**
   * Save a saved-query as a structured envelope: writes the body and the
   * sidecar in sequence. The body has to land first because Repos refuses
   * orphan sidecars (a sidecar against a non-existent body returns 404).
   *
   * On sidecar-failure-after-body-success the body save is left in place and
   * the error propagates — the caller can retry the sidecar later (the next
   * `saveStructured` will re-emit it). This trades momentary inconsistency
   * for write availability when the network glitches between calls.
   */
  const saveStructured = useCallback(
    async (
      projectId: string,
      owner: string,
      name: string,
      script: string,
      meta: EnvelopeMeta,
      overwrite: string = "overwrite"
    ): Promise<{ etag: string }> => {
      const wrapped = await wrapForSave({ script, meta })
      // Body first — the sidecar can only attach to an existing query file.
      const saved = await saveQuery(projectId, owner, name, overwrite, wrapped.body)
      // The Conductor answers a refused save (a name taken without
      // overwrite, among others) with 200 and the refusal as the body. Stop
      // there: the sidecar would land on the file that is already there.
      const refusal = refusalOf(saved.data)
      if (refusal !== null) throw new Error(refusal)
      // Plain-script saves (kind === "script") still write a sidecar so we
      // get a savedAt + etag for free; the cost is one extra POST per save.
      // If it ever shows up as a hot path we can short-circuit on `script`
      // kind, but consistency is more valuable than the savings today.
      await saveQueryMeta(projectId, owner, name, wrapped.sidecar)
      // Reflect the just-saved record in the cached list so the post-save
      // refresh path (fetchQueries + dependent useEffects) hits the cache
      // instead of re-fetching every envelope. No-op when nothing is cached.
      const envelope: SavedQueryEnvelope = {
        schemaVersion: 1,
        kind: meta.kind,
        script,
        savedAt: new Date().toISOString(),
        etag: wrapped.etag,
        meta,
        name,
      }
      queryClient.setQueryData<SavedQueryEnvelope[]>(
        queryKeys.savedQueries.list(projectId, owner),
        (old) => {
          if (!old) return undefined
          const idx = old.findIndex((e) => e.name === name)
          const next = old.slice()
          if (idx >= 0) next[idx] = envelope
          else next.push(envelope)
          return next
        }
      )
      // A new query joins the names list (Provenance lineage): read it again
      queryClient.invalidateQueries({
        queryKey: queryKeys.savedQueries.names(projectId, owner),
      })
      return { etag: wrapped.etag }
    },
    [saveQuery, saveQueryMeta, queryClient]
  )

  /**
   * Load a structured envelope. Fetches body, then sidecar; falls back to a
   * synthetic "kind: script" envelope when the sidecar is missing (legacy
   * record) or unparseable (hand-corrupted in the Console).
   *
   * The Repos backend reports "no sidecar" by returning HTTP 200 with an
   * "Error NNNN: ..." body rather than 404, so we sniff the response text
   * for that shape in addition to catching real 404s.
   *
   * staleTime 0 keeps the old semantics: concurrent callers share one
   * request (single-flight), but every fresh call re-reads the backend.
   */
  const getStructured = useCallback(
    (
      projectId: string,
      owner: string,
      name: string
    ): Promise<SavedQueryEnvelope> =>
      queryClient.fetchQuery({
        queryKey: queryKeys.savedQueries.detail(projectId, owner, name),
        staleTime: 0,
        queryFn: async (): Promise<SavedQueryEnvelope> => {
          const bodyResp = await getQuery(projectId, owner, name)
          const rawBody =
            typeof bodyResp.data === "string" ? bodyResp.data : ""
          // Same 200-with-error-text trick on the body endpoint: ignore the
          // payload if it's the "Error NNNN: ..." sentinel.
          const body = isRepoErrorBody(rawBody) ? "" : rawBody
          let sidecar: string | null = null
          try {
            const metaResp = await getQueryMeta(projectId, owner, name)
            const raw =
              typeof metaResp.data === "string" ? metaResp.data : null
            sidecar = isRepoErrorBody(raw) ? null : raw
          } catch (err: unknown) {
            // 404 from the meta endpoint = no sidecar = legacy flat script.
            // Anything else (network, 500, auth) we propagate; the caller can
            // surface a warning while still falling back to script kind.
            if (!is404(err)) {
              throw err
            }
            sidecar = null
          }
          return tryUnwrap({ name, body, sidecar })
        },
      }),
    [queryClient, getQuery, getQueryMeta]
  )

  /**
   * List every saved query in the project as parsed envelopes — metadata only,
   * no script bodies. Uses the Conductor `queryfile-meta-list` bulk endpoint so
   * the saved-queries picker / Transform hydration / SavedQueries view costs
   * one HTTP round-trip per project rather than N×(body+sidecar) GETs.
   *
   * The envelopes returned here have `script: ""`. Callers that actually need
   * the script (opening a Console query into the editor, running a transform
   * pipeline body) must call `getStructured(name)` for that one record.
   */
  const listStructured = useCallback(
    (projectId: string, owner: string): Promise<SavedQueryEnvelope[]> =>
      queryClient.fetchQuery({
        queryKey: queryKeys.savedQueries.list(projectId, owner),
        staleTime: LIST_STALE_MS,
        queryFn: async (): Promise<SavedQueryEnvelope[]> => {
          const resp = await getQueryMetaList(projectId, owner)
          const entries = Array.isArray(resp.data) ? resp.data : []
          const out: SavedQueryEnvelope[] = []
          for (const entry of entries) {
            if (!entry || typeof entry.name !== "string") continue
            // The backend already serialised the sidecar inline as a JSON
            // value; re-stringify so `tryUnwrap` can run its schema checks.
            const sidecar =
              entry.meta != null ? JSON.stringify(entry.meta) : null
            try {
              const env = await tryUnwrap({
                name: entry.name,
                body: "",
                sidecar,
              })
              out.push(env)
            } catch {
              // Skip entries whose sidecar fails to schema-validate — they'll
              // reappear in the legacy / corrupt bucket on the next individual
              // open if the user clicks them.
            }
          }
          return out
        },
      }),
    [queryClient, getQueryMetaList]
  )

  return {
    getProjectQueries,
    isLoadingQueries,
    deleteQuery,
    getQuery,
    saveStructured,
    getStructured,
    listStructured,
  }
}

/** The message of a refused save's body (`{"Err msg": ...}` or "Error NNNN: ..."), or null. */
function refusalOf(body: unknown): string | null {
  const msg = errMsgOf(body)
  if (msg !== null) return msg
  return typeof body === "string" && isRepoErrorBody(body) ? body.trim() : null
}

function is404(err: unknown): boolean {
  return isAxiosError(err) && err.response?.status === 404
}

/** The Repos service reports "not found" by returning HTTP 200 with an
 *  "Error NNNN: ..." text body. Anything starting with that token, after a
 *  trim, is treated as a soft failure equivalent to 404. */
function isRepoErrorBody(raw: string | null): boolean {
  if (!raw) return false
  const trimmed = raw.trimStart()
  return /^Error\s+\d+:/i.test(trimmed)
}
